import { useEffect, useEffectEvent, useId, useRef, type ReactNode } from "react";
import { t } from "../i18n";
import { touchScreen } from "../lib/touch";

interface DialogProps {
  title: ReactNode;
  subtitle?: ReactNode;
  size?: "pin" | "narrow" | "normal" | "checkout" | "wide" | "product" | "floor" | "gate" | "legal";
  /** Buttons in the title bar, before the × (e.g. "⇄ Tafel verplaatsen"). */
  actions?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  /** Cannot be closed (no ×, no Escape, no click outside), e.g. the terms until they are accepted. */
  locked?: boolean;
  children: ReactNode;
}

/** Open dialogs, most recent last: Escape closes only the one on top (e.g. the code window). */
const openDialogs: symbol[] = [];

/** A dialog is open: the keys of the screen behind it wait (prototype: a modal takes the keys). */
export const dialogOpen = (): boolean => openDialogs.length > 0;

/** A field that brings up the keyboard of a phone. */
const typesText = (element: HTMLElement) =>
  element.matches(
    "textarea, select, [contenteditable], input:not([type=checkbox], [type=radio], [type=button], [type=submit], [type=range])",
  );

/**
 * A modal dialog: Escape and the × close it, focus moves inside and returns afterwards, and the
 * page behind is inert for screen readers. Dialogs can stack (a code window over the checkout).
 */
export function Dialog({ title, subtitle, size = "normal", actions, onClose, footer, locked, children }: DialogProps) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const closeOnEscape = useEffectEvent(onClose);
  const lockedNow = useEffectEvent(() => locked === true);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // the element marked data-autofocus wins; otherwise the first field or button
    const first =
      ref.current?.querySelector<HTMLElement>("[data-autofocus]") ??
      ref.current?.querySelector<HTMLElement>("input, textarea, button:not(.x)");
    // without scrolling to it: a phone dragged the list of a product to "Toevoegen" at the bottom, so the
    // choices at the top (Keuze pasta) were out of sight (Furkan, 18/10/2026)
    // on a phone or tablet a text field is not chosen for you: the keyboard would cover half the dialog
    // (Afrekenen on the iPhone, Furkan 18/10/2026); a tap on the field opens it. A PC keeps typing at once.
    if (first && typesText(first) && touchScreen()) ref.current?.focus({ preventScroll: true });
    else first?.focus({ preventScroll: true });
    const me = Symbol("dialog");
    openDialogs.push(me);
    // the screen behind does not scroll while a dialog is open: a finger moves the dialog, never the page
    document.documentElement.classList.add("has-dialog");
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && openDialogs.at(-1) === me && !lockedNow()) {
        event.preventDefault();
        closeOnEscape();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      openDialogs.splice(openDialogs.indexOf(me), 1);
      if (openDialogs.length === 0) document.documentElement.classList.remove("has-dialog");
      previous?.focus({ preventScroll: true });
    };
  }, []);

  return (
    <div
      className="scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !locked) onClose();
      }}
    >
      <div
        ref={ref}
        className={`modal ${size === "normal" ? "" : size}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-labelledby={titleId}
      >
        <div className="mhead">
          <h2 id={titleId}>
            {title}
            {subtitle !== undefined && <span className="sub">{subtitle}</span>}
          </h2>
          {actions}
          {!locked && (
            <button type="button" className="x" onClick={onClose} aria-label={t.product.close}>
              ×
            </button>
          )}
        </div>
        <div className="mbody">{children}</div>
        {footer !== undefined && <div className="mfoot">{footer}</div>}
      </div>
    </div>
  );
}
